import {
  TurbineWorkspaceMapping,
  NewTurbineWorkspaceMapping,
  UpdateTurbineWorkspaceMapping,
  BulkTurbineWorkspaceMapping,
  BulkSiteTurbineWorkspaceMapping,
  TurbineWorkspaceMappingFilter,
  TurbineWorkspaceMappingsResponse,
  TurbineWithMapping,
  TurbineWithMappingsResponse,
  MappingStats,
  CSVMappingRow,
  CSVImportResult,
  CreateConfigFromCSVResult,
  validateComponentSelection,
} from '@/types/turbineWorkspaceMapping';
import { turbinesApi } from './turbines';
import { workspacesApi } from './workspacesApi';
import { sitesApi } from './sites';
import { filterOptionsService } from './filterOptions';
import { vblApi } from './vblApi';
import { isConfigEnabled } from '@/config/config';

const STORAGE_KEY = 'turbine_workspace_mappings';

class TurbineWorkspaceMappingsApiService {
  private generateId(): string {
    return `mapping_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async delay(ms: number = 300): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async convertComponentNameToId(componentName?: string): Promise<string | undefined> {
    if (!componentName || componentName.trim() === '') return undefined;

    try {
      const filterMappings = await filterOptionsService.getFilterMappings();
      return filterMappings.components[componentName];
    } catch (error) {
      console.warn('Failed to get component ID for name:', componentName);
      return undefined;
    }
  }

  private async convertMajorComponentNameToId(majorComponentName?: string): Promise<string | undefined> {
    if (!majorComponentName || majorComponentName.trim() === '') return undefined;

    try {
      const filterMappings = await filterOptionsService.getFilterMappings();
      return filterMappings.majorComponents[majorComponentName];
    } catch (error) {
      console.warn('Failed to get major component ID for name:', majorComponentName);
      return undefined;
    }
  }

  private async convertComponentIdToName(componentId?: string): Promise<string | undefined> {
    if (!componentId || componentId.trim() === '') return undefined;

    try {
      const filterMappings = await filterOptionsService.getFilterMappings();
      // Reverse lookup: find name by ID
      for (const [name, id] of Object.entries(filterMappings.components)) {
        if (id === componentId) return name;
      }
      return undefined;
    } catch (error) {
      console.warn('Failed to get component name for ID:', componentId);
      return undefined;
    }
  }

  private async convertMajorComponentIdToName(majorComponentId?: string): Promise<string | undefined> {
    if (!majorComponentId || majorComponentId.trim() === '') return undefined;

    try {
      const filterMappings = await filterOptionsService.getFilterMappings();
      // Reverse lookup: find name by ID
      for (const [name, id] of Object.entries(filterMappings.majorComponents)) {
        if (id === majorComponentId) return name;
      }
      return undefined;
    } catch (error) {
      console.warn('Failed to get major component name for ID:', majorComponentId);
      return undefined;
    }
  }

  private getMappingsFromStorage(): TurbineWorkspaceMapping[] {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  private saveMappingsToStorage(mappings: TurbineWorkspaceMapping[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mappings));
  }

  async getMappings(
    filters: TurbineWorkspaceMappingFilter & { page?: number; size?: number } = {}
  ): Promise<TurbineWorkspaceMappingsResponse> {
    // Use real API if not in mock mode
    if (!isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return vblApi.getMappings(filters);
    }

    await this.delay();

    let mappings = this.getMappingsFromStorage();

    if (filters.site_id) {
      mappings = mappings.filter(m => m.site_id === filters.site_id);
    }
    if (filters.workspace_id) {
      mappings = mappings.filter(m => m.workspace_id === filters.workspace_id);
    }
    if (filters.turbine_id) {
      mappings = mappings.filter(m => m.turbine_id === filters.turbine_id);
    }
    if (filters.component_name) {
      mappings = mappings.filter(m => m.component_name === filters.component_name);
    }
    if (filters.major_component_name) {
      mappings = mappings.filter(m => m.major_component_name === filters.major_component_name);
    }

    const page = filters.page || 1;
    const size = filters.size || 20;
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    const paginatedMappings = mappings.slice(startIndex, endIndex);

    return {
      mappings: paginatedMappings,
      total_count: mappings.length,
      total_pages: Math.ceil(mappings.length / size),
      page,
      size,
      has_more: endIndex < mappings.length,
    };
  }

  async getTurbinesWithMappings(
    filters: TurbineWorkspaceMappingFilter & { page?: number; size?: number } = {}
  ): Promise<TurbineWithMappingsResponse> {
    // Use real API if not in mock mode
    if (!isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return vblApi.getTurbinesWithMappings(filters);
    }

    await this.delay();

    const turbinesResponse = await turbinesApi.getTurbines({
      page: filters.page,
      size: filters.size,
      site_id: filters.site_id,
    });

    const mappings = this.getMappingsFromStorage();

    // Since turbines can have multiple mappings, we need to create one TurbineWithMapping per mapping
    // For turbines without mappings, create a single entry with mapping: undefined
    const turbinesWithMappings: TurbineWithMapping[] = [];

    turbinesResponse.turbines.forEach(turbine => {
      const turbineMappings = mappings.filter(m => m.turbine_id === turbine.turbine_id);

      if (turbineMappings.length === 0) {
        // No mappings for this turbine
        turbinesWithMappings.push({
          turbine_id: turbine.turbine_id,
          turbine_name: turbine.turbine_name,
          site_id: turbine.site_id,
          site_name: turbine.site_name,
          cod_date: turbine.cod_date,
          model_name: turbine.model_name,
          manufacturer_name: turbine.manufacturer_name,
          mapping: undefined,
        });
      } else {
        // Create one entry per mapping
        turbineMappings.forEach(mapping => {
          turbinesWithMappings.push({
            turbine_id: turbine.turbine_id,
            turbine_name: turbine.turbine_name,
            site_id: turbine.site_id,
            site_name: turbine.site_name,
            cod_date: turbine.cod_date,
            model_name: turbine.model_name,
            manufacturer_name: turbine.manufacturer_name,
            mapping,
          });
        });
      }
    });

    return {
      turbines: turbinesWithMappings,
      total_count: turbinesWithMappings.length, // Count of turbine-mapping rows, not unique turbines
      total_pages: Math.ceil(turbinesWithMappings.length / (turbinesResponse.size || 20)),
      page: turbinesResponse.page,
      size: turbinesResponse.size,
      has_more: turbinesWithMappings.length > (turbinesResponse.size || 20),
    };
  }

  async createMapping(mappingData: NewTurbineWorkspaceMapping, userId?: string): Promise<TurbineWorkspaceMapping> {
    // Use real API if not in mock mode
    if (!isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      if (!userId) {
        throw new Error('User ID is required for creating mappings');
      }

      // Convert component names to IDs
      const component_id = mappingData.component_name
        ? (await filterOptionsService.getFilterMappings()).components[mappingData.component_name]
        : undefined;
      const major_component_id = mappingData.major_component_name
        ? (await filterOptionsService.getFilterMappings()).majorComponents[mappingData.major_component_name]
        : undefined;

      return vblApi.createMapping({
        turbine_id: mappingData.turbine_id,
        workspace_id: mappingData.workspace_id,
        component_id,
        major_component_id,
      }, userId);
    }

    await this.delay();

    const mappings = this.getMappingsFromStorage();

    // Check for existing mapping with same turbine AND same component
    // Allow multiple mappings per turbine as long as they target different components
    const existingMapping = mappings.find(m =>
      m.turbine_id === mappingData.turbine_id &&
      m.component_name === mappingData.component_name &&
      m.major_component_name === mappingData.major_component_name
    );

    if (existingMapping) {
      throw new Error('Mapping already exists for this turbine and component combination');
    }

    const turbinesResponse = await turbinesApi.getTurbines({ page: 1, size: 1000 });
    const turbine = turbinesResponse.turbines.find(t => t.turbine_id === mappingData.turbine_id);
    if (!turbine) {
      throw new Error('Turbine not found');
    }

    const workspacesResponse = await workspacesApi.list({ page: 1, size: 1000 });
    const workspace = workspacesResponse.workspaces.find(w => w.id === mappingData.workspace_id);
    if (!workspace) {
      throw new Error('Workspace not found');
    }

    // Convert component names to IDs for internal storage (if needed by backend later)
    const componentId = await this.convertComponentNameToId(mappingData.component_name);
    const majorComponentId = await this.convertMajorComponentNameToId(mappingData.major_component_name);

    const newMapping: TurbineWorkspaceMapping = {
      mapping_id: this.generateId(),
      turbine_id: mappingData.turbine_id,
      turbine_name: turbine.turbine_name,
      site_id: turbine.site_id,
      site_name: turbine.site_name,
      workspace_id: mappingData.workspace_id,
      workspace_name: workspace.name,
      component_id: componentId,
      component_name: mappingData.component_name,
      major_component_id: majorComponentId,
      major_component_name: mappingData.major_component_name,
      created_at: new Date().toISOString(),
      created_by: 'mock_user_id',
    };

    mappings.push(newMapping);
    this.saveMappingsToStorage(mappings);

    return newMapping;
  }

  async updateMapping(mappingData: UpdateTurbineWorkspaceMapping, userId?: string): Promise<TurbineWorkspaceMapping> {
    // Use real API if not in mock mode
    if (!isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      if (!userId) {
        throw new Error('User ID is required for updating mappings');
      }

      // Convert component names to IDs
      const component_id = mappingData.component_name
        ? (await filterOptionsService.getFilterMappings()).components[mappingData.component_name]
        : undefined;
      const major_component_id = mappingData.major_component_name
        ? (await filterOptionsService.getFilterMappings()).majorComponents[mappingData.major_component_name]
        : undefined;

      // Note: UpdateTurbineWorkspaceMapping doesn't include turbine_id
      // but the backend API may need it. For now, we'll pass an empty string
      // since the backend should be able to resolve it from the mapping_id
      return vblApi.updateMapping(mappingData.mapping_id, {
        turbine_id: '', // Backend should resolve from mapping_id
        workspace_id: mappingData.workspace_id,
        component_id,
        major_component_id,
      }, userId);
    }

    await this.delay();

    const mappings = this.getMappingsFromStorage();
    const index = mappings.findIndex(m => m.mapping_id === mappingData.mapping_id);

    if (index === -1) {
      throw new Error('Mapping not found');
    }

    const workspacesResponse = await workspacesApi.list({ page: 1, size: 1000 });
    const workspace = workspacesResponse.workspaces.find(w => w.id === mappingData.workspace_id);
    if (!workspace) {
      throw new Error('Workspace not found');
    }

    // Convert component names to IDs for internal storage (if needed by backend later)
    const componentId = await this.convertComponentNameToId(mappingData.component_name);
    const majorComponentId = await this.convertMajorComponentNameToId(mappingData.major_component_name);

    mappings[index] = {
      ...mappings[index],
      workspace_id: mappingData.workspace_id,
      workspace_name: workspace.name,
      component_id: componentId,
      component_name: mappingData.component_name,
      major_component_id: majorComponentId,
      major_component_name: mappingData.major_component_name,
    };

    this.saveMappingsToStorage(mappings);
    return mappings[index];
  }

  async deleteMapping(mappingId: string): Promise<void> {
    // Use real API if not in mock mode
    if (!isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      // Find the mapping object by ID first
      const mappings = await this.getMappings();
      const mapping = mappings.mappings.find(m => m.mapping_id === mappingId);
      if (!mapping) {
        throw new Error(`Mapping with ID ${mappingId} not found`);
      }
      return vblApi.deleteMapping(mapping);
    }

    await this.delay();

    const mappings = this.getMappingsFromStorage();
    const filteredMappings = mappings.filter(m => m.mapping_id !== mappingId);

    if (filteredMappings.length === mappings.length) {
      throw new Error('Mapping not found');
    }

    this.saveMappingsToStorage(filteredMappings);
  }

  async bulkCreateMappings(bulkData: BulkTurbineWorkspaceMapping, userId?: string): Promise<TurbineWorkspaceMapping[]> {
    // Use real API if not in mock mode
    if (!isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      if (!userId) {
        throw new Error('User ID is required for creating bulk mappings');
      }

      const component_id = await this.convertComponentNameToId(bulkData.component_name);
      const major_component_id = await this.convertMajorComponentNameToId(bulkData.major_component_name);

      return vblApi.createBulkMappings({
        turbine_ids: bulkData.turbine_ids,
        workspace_id: bulkData.workspace_id,
        component_id,
        major_component_id,
      }, userId);
    }

    // Mock implementation
    await this.delay(500);

    const createdMappings: TurbineWorkspaceMapping[] = [];

    for (const turbineId of bulkData.turbine_ids) {
      try {
        const mapping = await this.createMapping({
          turbine_id: turbineId,
          workspace_id: bulkData.workspace_id,
          component_name: bulkData.component_name,
          major_component_name: bulkData.major_component_name,
        });
        createdMappings.push(mapping);
      } catch (error) {
        console.warn(`Failed to create mapping for turbine ${turbineId}:`, error);
      }
    }

    return createdMappings;
  }

  async bulkCreateSiteMappings(bulkData: BulkSiteTurbineWorkspaceMapping, userId?: string): Promise<TurbineWorkspaceMapping[]> {
    // Use real API if not in mock mode
    if (!isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      if (!userId) {
        throw new Error('User ID is required for creating bulk site mappings');
      }

      // Get turbines for the site
      const turbinesResponse = await turbinesApi.getTurbines({
        page: 1,
        site_id: bulkData.site_id,
        size: 1000
      });

      let turbineIds = turbinesResponse.turbines.map(t => t.turbine_id);

      // Apply filters if provided
      if (bulkData.turbine_filter?.turbine_ids) {
        turbineIds = turbineIds.filter(id => bulkData.turbine_filter!.turbine_ids!.includes(id));
      }
      if (bulkData.turbine_filter?.exclude_turbine_ids) {
        turbineIds = turbineIds.filter(id => !bulkData.turbine_filter!.exclude_turbine_ids!.includes(id));
      }

      const component_id = await this.convertComponentNameToId(bulkData.component_name);
      const major_component_id = await this.convertMajorComponentNameToId(bulkData.major_component_name);

      return vblApi.createBulkMappings({
        turbine_ids: turbineIds,
        workspace_id: bulkData.workspace_id,
        component_id,
        major_component_id,
      }, userId);
    }

    // Mock implementation
    await this.delay(500);

    const turbinesResponse = await turbinesApi.getTurbines({ page: 1, site_id: bulkData.site_id, size: 1000 });
    let turbineIds = turbinesResponse.turbines.map(t => t.turbine_id);

    if (bulkData.turbine_filter?.turbine_ids) {
      turbineIds = turbineIds.filter(id => bulkData.turbine_filter!.turbine_ids!.includes(id));
    }

    if (bulkData.turbine_filter?.exclude_turbine_ids) {
      turbineIds = turbineIds.filter(id => !bulkData.turbine_filter!.exclude_turbine_ids!.includes(id));
    }

    return this.bulkCreateMappings({
      turbine_ids: turbineIds,
      workspace_id: bulkData.workspace_id,
      component_name: bulkData.component_name,
      major_component_name: bulkData.major_component_name,
    });
  }

  async getMappingStats(): Promise<MappingStats> {
    // Use real API if not in mock mode
    if (!isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return vblApi.getMappingStats();
    }

    await this.delay();

    const mappings = this.getMappingsFromStorage();
    const turbinesResponse = await turbinesApi.getTurbines({ page: 1, size: 10000 });
    const sitesResponse = await sitesApi.getSites({ page: 1, size: 1000 });

    const mappingsBySite: Record<string, {
      site_name: string;
      total_turbines: number;
      mapped_turbines: number;
    }> = {};

    sitesResponse.sites.forEach(site => {
      const siteTurbines = turbinesResponse.turbines.filter(t => t.site_id === site.site_id);
      const siteMappings = mappings.filter(m => m.site_id === site.site_id);
      // Count unique turbines that have at least one mapping
      const uniqueMappedTurbineIds = new Set(siteMappings.map(m => m.turbine_id));

      mappingsBySite[site.site_id] = {
        site_name: site.site_name,
        total_turbines: siteTurbines.length,
        mapped_turbines: uniqueMappedTurbineIds.size,
      };
    });

    const workspaceIds = new Set(mappings.map(m => m.workspace_id));
    // Count unique turbines that have at least one mapping
    const uniqueMappedTurbineIds = new Set(mappings.map(m => m.turbine_id));

    return {
      total_turbines: turbinesResponse.turbines.length,
      mapped_turbines: uniqueMappedTurbineIds.size,
      unmapped_turbines: turbinesResponse.turbines.length - uniqueMappedTurbineIds.size,
      total_workspaces_used: workspaceIds.size,
      mappings_by_site: mappingsBySite,
    };
  }

  async exportMappingsCSV(): Promise<string> {
    // Use real API if not in mock mode
    if (!isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return vblApi.exportMappingsCSV();
    }

    await this.delay();

    const mappings = this.getMappingsFromStorage();
    const headers = ['Site Name', 'Turbine Name', 'Workspace Name', 'Component Name', 'Major Component Name'];

    const csvRows = [
      headers.join(','),
      ...mappings.map(mapping => [
        `"${mapping.site_name}"`,
        `"${mapping.turbine_name}"`,
        `"${mapping.workspace_name}"`,
        `"${mapping.component_name || ''}"`,
        `"${mapping.major_component_name || ''}"`,
      ].join(','))
    ];

    return csvRows.join('\n');
  }

  async importMappingsCSV(csvContent: string, userId: string): Promise<CSVImportResult> {
    // Use real API if not in mock mode
    if (!isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return vblApi.importMappingsCSV(csvContent, userId);
    }

    await this.delay(1000);

    const lines = csvContent.split('\n').filter(line => line.trim());
    const dataRows = lines.slice(1);

    const successful: CSVMappingRow[] = [];
    const failed: { row: CSVMappingRow; errors: string[]; lineNum: number; }[] = [];
    let lineNum = 0;
    for (const line of dataRows) {
      lineNum++;
      const values = line.split(',').map(v => v.replace(/"/g, '').trim());

      const row: CSVMappingRow = {
        site_name: values[0] || '',
        turbine_name: values[1] || '',
        workspace_name: values[2] || '',
        component_name: values[3] || undefined,
        major_component_name: values[4] || undefined,
        owner: userId,
        line_num: lineNum
      };

      const errors: string[] = [];

      if (!row.site_name) errors.push('Site name is required');
      if (!row.turbine_name) errors.push('Turbine name is required');
      if (!row.workspace_name) errors.push('Workspace name is required');

      // Validate component selection using the validation function
      const componentValidationError = validateComponentSelection(
        row.component_name,
        row.major_component_name
      );
      if (componentValidationError) {
        errors.push(componentValidationError.message);
      }

      if (errors.length > 0) {
        failed.push({ row, errors, lineNum });
      } else {
        successful.push(row);
      }
    }

    return {
      successful,
      failed,
      summary: {
        total_rows: dataRows.length,
        successful_count: successful.length,
        failed_count: failed.length,
      },
    };
  }

  async createConfigFromCSV(validRows: CSVMappingRow[]): Promise<CreateConfigFromCSVResult> {
    // Use real API if not in mock mode
    if (!isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return vblApi.createConfigFromCSV(validRows);
    }

    await this.delay(1000);
    return {
      "total_rows": validRows.length,
      "total_success": validRows.length,
      "total_fail": 0,
      "warnings": []
    }
  }
}

export const turbineWorkspaceMappingsApi = new TurbineWorkspaceMappingsApiService();